import { createMultiAlgorithmPatches } from '@dabble/patches';
import { PatchesSync } from '@dabble/patches/net';
import { readonly, ref } from 'vue';
import { PUBLIC_PATCHES_URL } from './rest';

// Whether the live WS session authenticated with an `impersonate` target
// (token auth — App.vue passes it whenever PUBLIC_PATCHES_KEY is set). The
// server strips admin from impersonated token sessions (server API keys from
// config.keys excepted), so writes to other users' docs over this socket can
// be rejected with a 403 that is never logged server-side. Surfaces the real
// auth mode so panels doing such writes (e.g. instruction sends) can warn.
const impersonatedAuth = ref(false);
export const syncAuthImpersonated = readonly(impersonatedAuth);

// Base WebSocket URL (without user-specific path)
const WS_BASE_URL = PUBLIC_PATCHES_URL.replace(/^http/, 'ws');

/**
 * Create Patches and PatchesSync instances.
 * Call this before auth - the sync URL will be updated when connecting.
 */
export function createPatchesInstances() {
  const patches = createMultiAlgorithmPatches({ defaultAlgorithm: 'lww' });

  // Create sync with base URL - will be updated with adminUid before connecting
  const sync = new PatchesSync(patches, WS_BASE_URL);

  return { patches, sync };
}

interface AuthOptions {
  token: string;
  impersonate?: string;
}

interface AuthResult {
  uid: string;
}

/**
 * Connect sync after auth is ready.
 * Updates the URL with adminUid before connecting.
 */
export async function connectSync(sync: PatchesSync, adminUid: string, authOptions?: AuthOptions): Promise<void> {
  impersonatedAuth.value = Boolean(authOptions?.token && authOptions?.impersonate);

  // Update URL with the admin user's uid, self-identifying as the admin
  // console — pup stamps client+version into the conn registry, and the admin
  // user page then renders these rows as "Admin Console" instead of mystery
  // "Legacy Client" devices.
  sync.url = `${WS_BASE_URL}/ws/${adminUid}?client=dabble-admin&version=${encodeURIComponent(__APP_VERSION__)}`;

  sync.connect();

  let connected = false;
  await new Promise<void>((resolve, reject) => {
    sync.subscribe(async state => {
      const shouldAuth = connected === false && state.connected;
      connected = state.connected;
      if (state.connected) {
        if (shouldAuth && authOptions?.token) {
          try {
            const result = await sync.rpc!.call<AuthResult>('authenticate', {
              token: authOptions.token,
              impersonate: authOptions.impersonate,
            });
            console.log('WebSocket authenticated as:', result.uid);
            resolve();
          } catch (error) {
            console.error('WebSocket authentication failed:', error);
            sync.disconnect();
            reject(error);
          }
        } else {
          // Cookie auth - ready immediately on connect
          resolve();
        }
      }
    });
  });
}
